import { API_V1_BASE_URL } from "../../config/api";

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem("accessToken");

  const response = await fetch(`${API_V1_BASE_URL}${path}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
    ...options,
  });

  const result = await response.json();

  if (!response.ok || !result.success) {
    throw new Error(result.message || "Request failed");
  }

  return (result.data ?? []) as T;
}

export interface User {
  id: string;

  tenant_id: string;
  tenant_name?: string;
  tenant_email?: string;

  max_users: number;

  first_name: string;
  last_name: string;

  email: string;

  phone_number: string;

  role: string;
  department: string;
  status: string;

  created_by: any;

  created_at: string;
  last_login: string;
  deleted_at?: string | null;
}

export interface GetUsersParams {
  page?: number;
  limit?: number;
  status?: string;
  role?: string;
  search?: string;
  tenantId?: string;
}

export interface Role {
  id: string;
  tenantId: string;

  role: string;
  name: string;
  description: string;

  permissions: string[];
  pages: string[];
}

export interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  email: string;
  phone_number?: string;
  status: string;
  max_users?: number;
  logo_url?: string;
  storage_limit_value?: number | string;
  storage_limit_unit?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateUserBody {
  email: string;
  firstName: string;
  lastName: string;

  role: string;

  department: string;

  phoneNumber: string;

  // password: string;

  tenantId: string;
}

export interface UpdateUserBody {
  firstName?: string;
  lastName?: string;

  role?: string;

  department?: string;

  phoneNumber?: string;

  status?: string;
}

export interface CreateRoleBody {
  tenantId: string;

  role: string;

  name: string;

  description: string;

  permissions: string[];

  pages: string[];
}

export interface UpdateRoleBody {
  name?: string;

  description?: string;

  permissions?: string[];

  pages?: string[];
}

export interface CreateTenantBody {
  tenant: {
    name: string;
    subdomain: string;
    email: string;
    phoneNumber: string;
    maxUsers: number;
    logoUrl?: string;
    storageLimitValue?: number;
    storageLimitUnit?: string;
  };

  user: {
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    department: string;
    phoneNumber: string;
    // password: string;
  };
}
export interface UpdateTenantBody {
  name?: string;
  subdomain?: string;
  email?: string;
  phoneNumber?: string;
  maxUsers?: number;
  status?: string;
  logoUrl?: string;
  storageLimitValue?: number;
  storageLimitUnit?: string;
}

export interface MyTenantPlan {
  id: string;
  name: string;
  status: string;
  logo_url?: string;
  max_users: number;
  storage_limit_value: number | string;
  storage_limit_unit: string;
}

export const superAdminApi = {
  //---------------------------------------
  // USERS
  //---------------------------------------

  getUsers: (params: GetUsersParams = {}) => {
    const query = new URLSearchParams();

    if (params.page) query.set("page", String(params.page));

    if (params.limit) query.set("limit", String(params.limit));

    if (params.status && params.status !== "all")
      query.set("status", params.status);

    if (params.role && params.role !== "all") query.set("role", params.role);

    if (params.search) query.set("search", params.search);

    if (params.tenantId) query.set("tenantId", params.tenantId);

    return request(`/admin/users${query.toString() ? `?${query}` : ""}`);
  },

  getUserById: (id: string) => request<User>(`/admin/users/${id}`),

  getMyProfile: () => request<User>("/users/me"),

  createUser: (body: CreateUserBody) =>
    request<User>("/admin/users", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateUser: (id: string, body: UpdateUserBody) =>
    request<User>(`/admin/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  deactivateUser: (id: string) =>
    request(`/admin/users/${id}/deactivate`, {
      method: "POST",
    }),

  reactivateUser: (id: string) =>
    request(`/admin/users/${id}/reactivate`, {
      method: "POST",
    }),

  deleteUser: (id: string) =>
    request(`/admin/users/${id}`, {
      method: "DELETE",
    }),

  // ---------------------------------------
  // AUDIT
  // ---------------------------------------

  getAuditLogs: () => request("/audit-logs"),

  getUserAuditLogs: (userId: string) => request(`/audit-logs/users/${userId}`),

  //---------------------------------------
  // ROLES
  //---------------------------------------

  getRoles: () => request<Role[]>("/admin/roles"),

  getRole: (id: string) => request<Role>(`/admin/roles/${id}`),

  createRole: (body: CreateRoleBody) =>
    request<Role>("/admin/roles", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateRole: (id: string, body: UpdateRoleBody) =>
    request<Role>(`/admin/roles/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  //---------------------------------------
  // TENANTS
  //---------------------------------------

  createTenant: (body: CreateTenantBody) =>
    request("/admin/tenants", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateTenant: (id: string, body: UpdateTenantBody) =>
    request<Tenant>(`/admin/tenants/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  // List tenants (id + name) for the "existing tenant" dropdown.
  getTenants: () =>
    request<Array<{ id: string; name: string }>>("/admin/tenants"),

  getTenantUsers: (tenantId: string, params: GetUsersParams = {}) => {
    const query = new URLSearchParams();

    if (params.page) query.set("page", String(params.page));

    if (params.limit) query.set("limit", String(params.limit));

    if (params.status) query.set("status", params.status);

    if (params.role) query.set("role", params.role);

    if (params.search) query.set("search", params.search);

    return request(
      `/admin/tenants/${tenantId}/users${query.toString() ? `?${query}` : ""}`,
    );
  },

  getMyUsers: (params: GetUsersParams = {}) => {
    const query = new URLSearchParams();

    if (params.page) query.set("page", String(params.page));

    if (params.limit) query.set("limit", String(params.limit));

    if (params.status) query.set("status", params.status);

    if (params.role) query.set("role", params.role);

    if (params.search) query.set("search", params.search);

    return request(`/admin/my/users${query.toString() ? `?${query}` : ""}`);
  },
  getMyTenant: () => request<MyTenantPlan>("/admin/my/tenant"),
  uploadTenantLogo: async (file: File, name: string) => {
    const formData = new FormData();
    formData.append("logo", file);
    formData.append("name", name); // <-- add this line
    const res = await fetch(`${API_V1_BASE_URL}/admin/tenants/upload-logo`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
      body: formData,
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.message);
    return json.data;
  },
};
